import React, { createContext, useContext, useReducer, useEffect } from 'react';
import axios from 'axios';

// Configuration API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

// Types d'actions
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  LOGOUT: 'LOGOUT'
};

// État initial
const initialState = {
  user: null,
  loading: true,
  error: null,
  isAuthenticated: false
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false,
        error: null
      };
    
    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    case AUTH_ACTIONS.LOGOUT:
      localStorage.removeItem('token');
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };
    
    default:
      return state;
  }
};

// Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Configuration Axios avec interceptors
  useEffect(() => {
    const token = localStorage.getItem('token');

    // Support mode DEMO (pas d'API requise)
    if (token === 'DEMO') {
      const demoUser = localStorage.getItem('userData');
      if (demoUser) {
        try {
          const user = JSON.parse(demoUser);
          dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
        } catch {}
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
      return; // ne configure pas axios en mode démo
    }

    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    // Intercepteur pour gérer les erreurs d'authentification
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Vérifier l'utilisateur connecté au chargement
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');

      // Mode DEMO: charger l'utilisateur depuis localStorage sans appeler l'API
      if (token === 'DEMO') {
        const demoUser = localStorage.getItem('userData');
        if (demoUser) {
          try {
            const user = JSON.parse(demoUser);
            dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
          } catch {
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
          }
        } else {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
        return;
      }

      if (!token) {
        // fallback: si un userData existe (démo) mais pas de token, l'utiliser
        const demoUser = localStorage.getItem('userData');
        if (demoUser) {
          try {
            const user = JSON.parse(demoUser);
            dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
            localStorage.setItem('token', 'DEMO');
            return;
          } catch {}
        }
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/auth/profile`);
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: response.data.user });
      } catch (error) {
        console.error('Erreur vérification auth:', error);
        localStorage.removeItem('token');
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    checkAuth();
  }, []);

  // Actions
  const login = async (credentials) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const response = await axios.post(`${API_BASE_URL}/auth/login`, credentials);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur de connexion';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const response = await axios.post(`${API_BASE_URL}/auth/register`, userData);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur d\'inscription';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  // Mode démo: permet de définir l'utilisateur sans appel API
  const loginDemo = (user) => {
    try {
      localStorage.setItem('token', 'DEMO');
      localStorage.setItem('userData', JSON.stringify(user));
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
      return { success: true };
    } catch (e) {
      return { success: false, error: 'Impossible de créer la session démo' };
    }
  };

  const logout = () => {
    delete axios.defaults.headers.common['Authorization'];
    // Nettoyage complet, y compris les clés de démo
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('userToken');
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const updateProfile = async (updates) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      const response = await axios.put(`${API_BASE_URL}/auth/profile`, updates);
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: response.data.user });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur de mise à jour';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const value = {
    ...state,
    login,
    register,
    loginDemo,
    logout,
    clearError,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Composant de protection des routes
export const ProtectedRoute = ({ children, fallback = null }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="auth-loading">
        <div className="loading-spinner">⏳ Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return fallback;
  }

  return children;
};

export default AuthContext;